import React, { PointerEvent, PureComponent } from 'react';
import { ICompetitionsTimeSliderProps, ICompetitionsTimeSliderState } from '../../interfaces';
import styles from './CompetitionEvolution.module.css';

export class CompetitionEvolution extends PureComponent<ICompetitionsTimeSliderProps, ICompetitionsTimeSliderState> {
	private readonly _containerRef = React.createRef<HTMLDivElement>();
	private readonly _startControlRef = React.createRef<HTMLDivElement>();
	private readonly _endControlRef = React.createRef<HTMLDivElement>();

	constructor(props: ICompetitionsTimeSliderProps) {
		super(props);
		this.state = {
			currentStartIndex: props.initStartIndex,
			currentEndIndex: props.initEndIndex,
			gap: 0,
			startPointLeft: 0,
			endPointLeft: 0,
			isStartDragActive: false,
			isEndDragActive: false,
			containerLeft: 0,
			containerRight: 0,
			isLeftControlSelected: false,
			isRightControlSelected: false,
			deltaDragX: 0,
			currentStartYearText: props.currentStartYearText,
			currentEndYearText: props.currentEndYearText
		};
	}

	public componentDidMount() {
		this._onSetDimensions(this.props.initStartIndex, this.props.initEndIndex);
	}

	public componentWillUnmount() {
		this._removeEventListeners();
	}

	public componentDidUpdate(prevProps: Readonly<ICompetitionsTimeSliderProps>): void {
		if (
			this.props.totalGaps !== prevProps.totalGaps ||
			this.props.startYear !== prevProps.startYear ||
			this.props.endYear !== prevProps.endYear
		) {
			const currentEndIndex = this.props.initEndIndex;
			this.setState({
				currentStartIndex: this.props.initStartIndex,
				currentEndIndex,
				currentStartYearText: this.props.currentStartYearText,
				currentEndYearText: this.props.currentEndYearText
			});
			this._onSetDimensions(this.props.initStartIndex, currentEndIndex);
		}
		if (
			(prevProps.viewPortX !== this.props.viewPortX || prevProps.viewPortY !== this.props.viewPortY)
		 ) {
			this._onSetDimensions(this.state.currentStartIndex, this.state.currentEndIndex);
		}
	}

	private _addEventListeners = () => {
		document.addEventListener('mousemove', this._onMouseMove, false);
		document.addEventListener('mouseup', this._onMouseUp, false);
		document.addEventListener('touchmove', this._onTouchMove, false);
		document.addEventListener('touchend', this._onTouchEnd, false);
	}

	private _removeEventListeners = () => {
		document.removeEventListener('mousemove', this._onMouseMove, false);
		document.removeEventListener('mouseup', this._onMouseUp, false);
		document.removeEventListener('touchmove', this._onTouchMove, false);
		document.removeEventListener('touchend', this._onTouchEnd, false);
	}

	private _onSetDimensions = (startIndex: number, endIndex: number) => {
		if (this._containerRef.current) {
			const containerBoundingClientRect = this._containerRef.current.getBoundingClientRect();
			const gap = (containerBoundingClientRect.width) / (this.props.totalGaps - 1);
			this.setState({
				containerLeft: containerBoundingClientRect.left,
				containerRight: containerBoundingClientRect.left + containerBoundingClientRect.width,
				gap,
				startPointLeft: startIndex * gap,
				endPointLeft: endIndex * gap
			});
		}
	}

	private _onTouchEnd = (event: TouchEvent) => {
		const deltaDragX = event.changedTouches[0].clientX- this.state.deltaDragX;
		this.setState({
			isStartDragActive: false,
			isEndDragActive: false,
			isRightControlSelected: deltaDragX === 0 ? this.state.isRightControlSelected : false,
			isLeftControlSelected: deltaDragX === 0 ? this.state.isLeftControlSelected : false,
			deltaDragX: 0
		});
		this._removeEventListeners();
	}

	private _onMouseUp = (event: MouseEvent) => {
		const deltaDragX = event.clientX- this.state.deltaDragX;
		this.setState({
			isStartDragActive: false,
			isEndDragActive: false,
			isRightControlSelected: deltaDragX === 0 ? this.state.isRightControlSelected : false,
			isLeftControlSelected: deltaDragX === 0 ? this.state.isLeftControlSelected : false,
			deltaDragX: 0
		});
		this._removeEventListeners();
	}

	// TODO debounce 150
	private _onChangeRangeDebounced = (currentStartIndex: number, currentEndIndex: number): void => {
		this.props.onChangeRange(currentStartIndex, currentEndIndex);
	};

	private _onMoveStartControl = (newStartIndex: number, maintainSelection?: boolean) => {
		const currentStartIndex = newStartIndex <= 0
			? 0
			: newStartIndex >= this.props.totalGaps - 1
				? this.props.totalGaps - 1
				: newStartIndex;
		const calculatedStartPointLeft = currentStartIndex * this.state.gap;
		const startPointLeft = calculatedStartPointLeft < 0
			? 0
			: calculatedStartPointLeft > (this.state.containerRight - this.state.containerLeft)
				? this.state.containerRight - this.state.containerLeft
				: calculatedStartPointLeft;

		let currentEndIndex = this.state.currentEndIndex;
		let endPointLeft = this.state.endPointLeft;
		if (currentEndIndex < currentStartIndex) {
			currentEndIndex = currentStartIndex + 1;
			const calculatedEndPointLeft = currentEndIndex * this.state.gap;
			endPointLeft = calculatedEndPointLeft < 0
				? 0
				: calculatedEndPointLeft > (this.state.containerRight - this.state.containerLeft)
					? this.state.containerRight - this.state.containerLeft
					: calculatedEndPointLeft;
		}
		
		this._onChangeRangeDebounced(currentStartIndex, currentEndIndex);
		this.setState({
			currentStartIndex,
			currentEndIndex,
			startPointLeft,
			endPointLeft,
			isRightControlSelected: maintainSelection ? this.state.isRightControlSelected : false,
			isLeftControlSelected: maintainSelection ? this.state.isLeftControlSelected : false
		});
	}

	private _onMoveEndControl = (newEndIndex: number, maintainSelection?: boolean) => {
		const currentEndIndex = newEndIndex < 0
			? 0
			: newEndIndex >= this.props.totalGaps - 1
				? this.props.totalGaps - 1
				: newEndIndex;
		const calculatedEndPointLeft = currentEndIndex * this.state.gap;
		const endPointLeft = calculatedEndPointLeft < 0
			? 0
			: calculatedEndPointLeft > (this.state.containerRight - this.state.containerLeft)
				? this.state.containerRight - this.state.containerLeft
				: calculatedEndPointLeft;

		let currentStartIndex = this.state.currentStartIndex;
		let startPointLeft = this.state.startPointLeft;	
		if (currentStartIndex > currentEndIndex) {
			currentStartIndex = currentEndIndex - 1;
			const calculatedStartPointLeft = currentStartIndex * this.state.gap;
			startPointLeft = calculatedStartPointLeft < 0
				? 0
				: calculatedStartPointLeft > (this.state.containerRight - this.state.containerLeft)
					? this.state.containerRight - this.state.containerLeft
					: calculatedStartPointLeft;
		}
		this._onChangeRangeDebounced(currentStartIndex,currentEndIndex);
		this.setState({
			currentEndIndex,
			endPointLeft,
			startPointLeft,
			currentStartIndex,
			isRightControlSelected: maintainSelection ? this.state.isRightControlSelected : false,
			isLeftControlSelected: maintainSelection ? this.state.isLeftControlSelected : false
		});
	}

	private _onMoveControl = (x: number) => {
		if (x > this.state.containerLeft && x < this.state.containerRight) {
			if (this.state.isStartDragActive) {
				const difference = Math.round((x - (this.state.startPointLeft + this.state.containerLeft)) / this.state.gap);
				const newStartIndex = this.state.currentStartIndex + difference;
				this._onMoveStartControl(newStartIndex);
			} else if (this.state.isEndDragActive) {
				const difference = Math.round((x - (this.state.endPointLeft + this.state.containerLeft)) / this.state.gap);
				const newEndIndex = this.state.currentEndIndex + difference;
				this._onMoveEndControl(newEndIndex);
			}
		}
	}

	private _onTouchMove = (event: TouchEvent) => {
		this._onMoveControl(event.changedTouches[0].clientX);
	}

	private _onMouseMove = (event: MouseEvent) => {
		this._onMoveControl(event.clientX);
	}

	private _onStartStartControlDrag = (event: PointerEvent<HTMLDivElement>) => {
		this.setState({
			isStartDragActive: true,
			isLeftControlSelected: !this.state.isLeftControlSelected,
			isRightControlSelected: false,
			deltaDragX: event.clientX
		});
		this._addEventListeners();
	}

	private _onStartEndControlDrag = (event: PointerEvent<HTMLDivElement>) => {
		this.setState({
			isEndDragActive: true,
			isLeftControlSelected: false,
			isRightControlSelected: !this.state.isRightControlSelected,
			deltaDragX: event.clientX
		});
		this._addEventListeners();
	}

	private _onStartLeftArrowClick = () => {
		if (this.state.currentStartIndex > 0) {
			this._onMoveStartControl(this.state.currentStartIndex - 1);
		}
	}

	private _onStartRightArrowClick = () => {
		if (this.state.currentStartIndex < this.props.totalGaps - 1) {
			this._onMoveStartControl(this.state.currentStartIndex + 1);
		}
	}

	private _onEndLeftArrowClick = () => {
		if (this.state.currentEndIndex > 0) {
			this._onMoveEndControl(this.state.currentEndIndex - 1);
		}
	}

	private _onEndRightArrowClick = () => {
		if (this.state.currentEndIndex < this.props.totalGaps - 1) {
			this._onMoveEndControl(this.state.currentEndIndex + 1);
		}
	}

	private _onMidBarContainerClick = (event: React.MouseEvent<HTMLDivElement>) => {
		if (this._containerRef.current) {
			const containerBoundingClientRect = this._containerRef.current.getBoundingClientRect();
			const eventX = event.clientX - containerBoundingClientRect.left;
			const indexOfEvent = Math.floor(eventX / this.state.gap);
			if (indexOfEvent <= this.state.currentStartIndex) {
				this._onMoveStartControl(indexOfEvent, true);
			} else if (indexOfEvent >= this.state.currentEndIndex) {
				this._onMoveEndControl(indexOfEvent, true);
			} else {
				if (this.state.isLeftControlSelected) {
					this._onMoveStartControl(indexOfEvent >= this.state.currentEndIndex ? this.state.currentEndIndex - 1 : indexOfEvent, true);
				} else if (this.state.isRightControlSelected) {
					this._onMoveEndControl(indexOfEvent <= this.state.currentStartIndex ? this.state.currentEndIndex + 1 : indexOfEvent, true);
				} else {
					const leftDifference = Math.abs(indexOfEvent - this.state.currentStartIndex);
					const rightDifference = Math.abs(indexOfEvent - this.state.currentEndIndex);
					if (rightDifference > leftDifference) {
						this._onMoveStartControl(indexOfEvent >= this.state.currentEndIndex ? this.state.currentEndIndex - 1 : indexOfEvent, true);
					} else {
						this._onMoveEndControl(indexOfEvent <= this.state.currentStartIndex ? this.state.currentEndIndex + 1 : indexOfEvent, true);
					}
				}
			}
		}
	}

	public render() {
		return (
			<div className={styles.competitionsTimeSliderContainer}>
				<div className={styles.dragAndDropContainer}>
					<div
						className={styles.competitionSliderControlContainer}
						ref={this._startControlRef}
						onPointerDown={this._onStartStartControlDrag}
						style={{
							backgroundColor: this.state.isLeftControlSelected ? '#F89504' : '#000',
							left: `${this.state.startPointLeft - 20 / 2}px`

						}}
					>
						<div className={styles.pointYearContainer}>
							{this.props.currentStartYearText}
						</div>
					</div>
					<div className={styles.midBarContainer}
						ref={this._containerRef}
						onClick={this._onMidBarContainerClick}
					/>
					<div
						className={styles.competitionSliderControlContainer}
						ref={this._endControlRef}
						onPointerDown={this._onStartEndControlDrag}
						style={{
							backgroundColor: this.state.isRightControlSelected ? '#F89504' : '#000',
							left: `${this.state.endPointLeft - 20 / 2}px`

						}}
					>
						<div className={styles.pointYearContainer}>
							{this.props.currentEndYearText}
						</div>
					</div>
				</div>
				<div className={styles.arrowsContainer}>
					<div className={styles.arrowIconContainer}>
						<i
							className={'fa fa-caret-left'}
							onClick={this._onStartLeftArrowClick}
						/>
						<div className={styles.startYearContainer}>
							{this.props.startYear}
						</div>
						<i
							className={'fa fa-caret-right'}
							onClick={this._onStartRightArrowClick}
						/>
					</div>
					<div className={styles.arrowIconContainerRight}>
						<i
							className={'fa fa-caret-right'}
							onClick={this._onEndRightArrowClick}
						/>
						<div className={styles.endYearContainer}>
							{this.props.endYear}
						</div>
						<i
							className={'fa fa-caret-left'}
							onClick={this._onEndLeftArrowClick}
						/>
					</div>
				</div>
			</div>
			
		);
	}
}
